import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { ExternalLink, Save, Search } from "lucide-react";
import { getData, setData } from "@/lib/indexed-db";
import { useAuth } from "@/providers/auth-provider";

const targetWindow = "searchVideo";
//@ts-ignore
const popupFeatures =
  "width=1024,height=768,scrollbars=yes,resizable=yes,status=yes,location=no,noopener,noreferrer";
const blankFeatures = "noopener,noreferrer";

export const TwoEmbed = () => {
  const { user } = useAuth();

  const [movieId, setMovieId] = useState("");

  const [tvId, setTvId] = useState("");
  const [season, setSeason] = useState("");
  const [episode, setEpisode] = useState("");

  const [animeTitle, setAnimeTitle] = useState("");
  const [animeEpisode, setAnimeEpisode] = useState("");

  const [url, setUrl] = useState("");
  const [urlList, setUrlList] = useState<any[]>([]);
  const [selectedUrl, setSelectedUrl] = useState("");
  const [saveName, setSaveName] = useState("");

  const playMovie = () => {
    setUrl(`https://www.2embed.cc/embed/${movieId}`);
  };

  const playTV = () => {
    if (season.trim() && episode.trim()) {
      setUrl(`https://www.2embed.cc/embedtv/${tvId}?s=${season}&e=${episode}`);
    } else {
      setUrl(`https://www.2embed.cc/embedtvfull/${tvId}`);
    }
  };

  const playAnime = () => {
    setUrl(`https://2anime.xyz/embed/${animeTitle}-${animeEpisode}`);
  };

  const saveUrl = async () => {
    const newUrlList = [
      ...urlList,
      {
        name: saveName,
        url: url,
      },
    ];
    await updateUrlList(newUrlList);
    setSaveName("");
  };

  const loadUrl = () => {
    setUrl(selectedUrl);
  };

  const deleteUrl = async () => {
    const newUrlList = urlList.filter((item) => item.url !== selectedUrl);
    await updateUrlList(newUrlList);
    setSelectedUrl("");
  };

  const updateUrlList = async (list: any[]) => {
    await setData("users", `${user._id}-2embed`, list);
    setUrlList(list);
  };

  useEffect(() => {
    const loadData = async () => {
      const result = await getData("users", `${user._id}-2embed`);
      setUrlList(result?.data || []);
    };

    loadData();
  }, []);

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      <div className="flex w-full gap-2 py-1 pl-12 pr-1 border-b">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1">
              Movie
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto m-1">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Movie</h4>
                <p className="text-sm text-muted-foreground">
                  IMDB, TVDB의 ID를 입력해주세요.
                </p>
              </div>
              <div className="grid gap-2">
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label htmlFor="id">ID</Label>
                  <Input
                    value={movieId}
                    onChange={(e) => setMovieId(e.target.value)}
                    placeholder="Enter the ID"
                    className="col-span-2 h-8 focus-visible:ring-0"
                  />
                </div>
                <div className="grid items-center gap-4">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={playMovie}
                    disabled={!movieId.trim()}
                  >
                    Play
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1">
              TV
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto m-1">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">TV 시리즈</h4>
                <p className="text-sm text-muted-foreground">
                  IMDB, TVDB의 ID를 입력해주세요.
                </p>
                <p className="text-sm text-muted-foreground">
                  시리즈 전체를 보려면 ID만 입력하세요.
                </p>
              </div>
              <div className="grid gap-2">
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label htmlFor="id">ID</Label>
                  <Input
                    value={tvId}
                    onChange={(e) => setTvId(e.target.value)}
                    placeholder="Enter the ID"
                    className="col-span-2 h-8 focus-visible:ring-0"
                  />
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label htmlFor="season">Season</Label>
                  <Input
                    value={season}
                    onChange={(e) => setSeason(e.target.value)}
                    placeholder="Enter the season"
                    className="col-span-2 h-8 focus-visible:ring-0"
                  />
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label htmlFor="episode">Episode</Label>
                  <Input
                    value={episode}
                    onChange={(e) => setEpisode(e.target.value)}
                    placeholder="Enter the episode"
                    className="col-span-2 h-8 focus-visible:ring-0"
                  />
                </div>
                <div className="grid items-center gap-4">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={playTV}
                    disabled={!tvId.trim()}
                  >
                    Play
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1">
              Anime
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto m-1">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Animation</h4>
                <p className="text-sm text-muted-foreground">
                  애니메이션의 제목과 에피소드를 입력해주세요.
                </p>
              </div>
              <div className="grid gap-2">
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    value={animeTitle}
                    onChange={(e) => setAnimeTitle(e.target.value)}
                    placeholder="Enter the title"
                    className="col-span-2 h-8 focus-visible:ring-0"
                  />
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label htmlFor="episode">Episode</Label>
                  <Input
                    value={animeEpisode}
                    onChange={(e) => setAnimeEpisode(e.target.value)}
                    placeholder="Enter the episode"
                    className="col-span-2 h-8 focus-visible:ring-0"
                  />
                </div>
                <div className="grid items-center gap-4">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={playAnime}
                    disabled={!animeTitle.trim() || !animeEpisode.trim()}
                  >
                    Play
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button size="icon" variant="outline">
              <Save className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto m-1">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Save & Load</h4>
                <p className="text-sm text-muted-foreground">
                  보고 있는 영상을 저장하거나 불러오세요.
                </p>
              </div>
              <div className="grid gap-2">
                <div className="grid grid-cols-3 items-center gap-4">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="Enter the name"
                    className="col-span-2 h-8 focus-visible:ring-0"
                  />
                </div>
                <div className="grid items-center">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={saveUrl}
                    disabled={!saveName.trim() || !url.trim()}
                  >
                    Save
                  </Button>
                </div>
                <div className="grid items-center">
                  <Select
                    value={selectedUrl}
                    onValueChange={(v) => setSelectedUrl(v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a Video" />
                    </SelectTrigger>
                    <SelectContent>
                      {urlList.map((item, index) => (
                        <SelectItem key={index} value={item.url}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid items-center">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={loadUrl}
                    disabled={!selectedUrl.trim()}
                  >
                    Load
                  </Button>
                </div>
                <div className="grid items-center">
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={deleteUrl}
                    disabled={!selectedUrl.trim()}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="outline">
              <Search className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              onClick={() =>
                window.open(
                  "https://www.imdb.com/",
                  targetWindow,
                  blankFeatures,
                )
              }
            >
              <ExternalLink className="w-4 h-4" />
              IMDB
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                window.open(
                  "https://www.themoviedb.org",
                  targetWindow,
                  blankFeatures,
                )
              }
            >
              <ExternalLink className="w-4 h-4" />
              TVDB
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex-1 p-1 overflow-hidden">
        {url && (
          <iframe
            src={url}
            width="100%"
            height="100%"
            allowFullScreen={true}
          ></iframe>
        )}
      </div>
    </div>
  );
};
